// Copyright (c) Docugami, Inc. All rights reserved.

import axios, { Method } from "axios";
import { DgDirectory, DgFile, DirectoryProperties, FileManagerInstance, FileProperties } from "filemanager/FileManager";
import { ClusterDocument, FileInfo } from "networking/api";

export interface FilePathBreadCrumb {
    text: string;
    key: string;
}

export const CLUSTER_CHOOSER_NO_DOC_SET_KEY = "_unassign_"
export const CLUSTER_CHOOSER_NEW_DOC_SET_KEY = "_create_"

export const supportedExtensions = [".pdf", ".docx", ".doc"];

export function getFileUxName(path: string, stripExtension: boolean = false) {
    let uxName = path;
    let index = path.lastIndexOf("/");
    if (index !== -1 && index !== path.length - 1) {
        uxName = uxName.substring(index + 1);
    }

    if (stripExtension) {
        index = path.lastIndexOf('.');
        if (index !== -1) {
            uxName = uxName.substring(0, index);
        }
    }

    return uxName;
}

export function getExtension(path: string): string {
    const index = path.lastIndexOf('.');
    if (index !== -1) {
        return path.substring(index);
    }

    return '';
}

export function isExtensionSupported(extension: string) {
    return supportedExtensions.indexOf(extension.toLowerCase()) !== -1;
}

export function isAncestorPath(ancestorPath: string, descendantPath?: string) {
    if (!ancestorPath) {
        // Root
        return true;
    }

    if (!descendantPath) {
        return false;
    }

    return descendantPath !== ancestorPath && descendantPath.startsWith(ancestorPath);
}

export function isAncestor(ancestorPath: string, descendant: FileProperties | DirectoryProperties) {
    return isAncestorPath(ancestorPath, descendant.path) || isAncestorPath(ancestorPath, descendant.displayName);
}

export function isChildPath(parentPath: string, childPath?: string) {
    if (!childPath) {
        // Root
        return false;
    }

    const childParentPath = getParentDirectoryPath(childPath);
    return parentPath === childParentPath;
}

export function isChild(parentPath: string, child: FileProperties | DirectoryProperties) {
    return isChildPath(parentPath, child.path) || isChildPath(parentPath, child.displayName);
}

/**
 * Check if an item is a direct child of a parent directory by ID.
 * @param parentId The parent directory ID
 * @param child The child item properties
 * @returns true if the child's parentId matches the given parentId
 */
export function isChildById(parentId: string, child: FileProperties | DirectoryProperties) {
    return child.parentId === parentId;
}

export interface FolderId {
    clusterId: string;
    fileId: string;
    value: string;
}

export const createFolderId = (value: string): FolderId => {
    if (!value) {
        throw new Error("Invalid folderId");
    }

    const parts = value.split(':');
    if (parts.length !== 2) {
        throw new Error("Invalid number of parts in folderId");
    }

    if (parts.includes('')) {
        throw new Error("Empty part in folderId");
    }

    return {
        clusterId: parts[0],
        fileId: parts[1],
        value: value
    }
}

export const createFolderIdString = (clusterId: string, fileId: string) => `${clusterId}:${fileId}`;

export function getFileSizeStringForKb(sizeKb: number): string {
    let sizeString = '';

    if (sizeKb < 1024) {
        sizeString = sizeKb + "KB"
    } else {
        sizeString = Math.floor(sizeKb / 1024) + "MB"
    }

    return sizeString;
}

export function getDirectoryPath(path: string): string {
    if (!path) {
        return '';
    }

    if (path[path.length - 1] === '/') {
        return path;
    }

    return path.substring(0, path.lastIndexOf('/') + 1)
}

export function getParentDirectoryPath(path: string): string {
    if (!path) {
        return '';
    }

    if (path[path.length - 1] === '/') {
        path = path.substring(0, path.length - 1);
    }

    return getDirectoryPath(path);
}

export function getDirectoryName(dirPath: string): string {
    if (!!!dirPath || dirPath[dirPath.length - 1] !== '/') {
        return '';
    }

    let path = dirPath.substring(0, dirPath.length - 1);
    return path.substring(path.lastIndexOf('/') + 1);
}

export async function isDirectory(id: string): Promise<boolean> {
    return !(await FileManagerInstance.isFileId(id));
}

export function getFileName(path: string): string {
    if (!!!path) {
        return '';
    }

    if (path[path.length - 1] === '/') {
        return '';
    }

    return path.substring(path.lastIndexOf('/') + 1);
}

export const UnassignedDocSetId = 'UNASSIGNED';

export function isUnassigned(documentId?: string): boolean {
    return !documentId || !!documentId?.startsWith(UnassignedDocSetId);
}

export const downloadFileFromUrl = async (url: string, fileName: string, method: Method, data?: any, addExtension: boolean = false) => {
    const response = await axios.request({
        method: method,
        url: url,
        data: data,
        responseType: "blob"
    });

    if (addExtension) {
        let contentType;
        for (const [key, value] of Object.entries(response.headers)) {
            if (typeof key === 'string' && key.toLowerCase() === 'content-type') {
                contentType = value;
                break;
            }
        }

        switch (contentType) {
            case "application/msword":
                fileName += ".doc";
                break;

            case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                fileName += ".docx";
                break;

            case "application/pdf":
                fileName += ".pdf";
                break;

            case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                fileName += ".xlsx";
                break;

            case "application/xml":
                fileName += ".xml";
                break;

            case "application/zip":
                fileName += ".zip";
                break;

            default:
                console.error("unknown content type for extension mapping: " + contentType);
                break;
        }
    }

    const a = document.createElement('a');
    a.href = URL.createObjectURL(response.data);
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(a.href);
}

export function isSupportedFile(fileName: string): boolean {
    const extension = getExtension(fileName);
    return !fileName.match(/^\./) &&
        !fileName.match(/^~\$/) &&
        isExtensionSupported(extension);
}

export function isSupportedFilePath(filePath: string): boolean {
    return !filePath.match(/.*\.\\/) &&
        !filePath.match(/.*\.\//);
}

export const isDgFile = (file: DgFile | DgDirectory): file is DgFile => file instanceof DgFile;

export const isDgDirectory = (file: DgFile | DgDirectory): file is DgDirectory => file instanceof DgDirectory;

export const createFileInfoFromDocument = (document: ClusterDocument): FileInfo => ({
    id: document.metadataId,
    clusterId: document.clusterId,
    name: document.fileId,
    displayName: document.displayName,
    hasCpml: !!document.hasCpml,
    status: document.status,
    ingestionIssues: document.ingestionIssues,
    ingestionStatus: document.ingestionStatus,
    ingestionWarnings: document.ingestionWarnings,
    sizeBytes: document.sizeBytes ?? 0,
    sizeKiloBytes: !!document.sizeBytes ? Math.round(document.sizeBytes / 1024) : 0,
    createdAt: document.createdAt
})
